<section class="flex w-full flex-col gap-y-5">
  <!-- Header -->
  <header class="flex flex-col gap-y-4 sm:flex-row sm:items-end sm:justify-between sm:gap-x-6">
    <div class="flex flex-col gap-y-1">
      <p
        class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase dark:text-brand-studio"
      >
        Posted to TikTok
      </p>
      <h1 class="m-0 text-2xl font-bold text-primary">Every video your creators have posted.</h1>
      <p class="m-0 max-w-2xl text-sm text-secondary">
        Live organic performance, moderation status and Spark authorisation in one place. Pick a
        winner and put budget behind it.
      </p>
    </div>

    <!-- Filter pills -->
    @if (totalCount() > 0) {
      <nav
        aria-label="Filter creator videos"
        class="flex shrink-0 flex-row items-center gap-x-1 rounded-full border border-border bg-card p-1"
      >
        @for (filter of filters; track filter.key) {
          <button
            type="button"
            (click)="selectFilter(filter.key)"
            [attr.aria-current]="store.libraryFilter() === filter.key ? 'true' : null"
            class="cursor-pointer rounded-full px-3 py-1.5 text-sm font-semibold transition-colors"
            [class]="getFilterClass(filter.key)"
          >
            {{ filter.label }}
            <span class="tabular-nums opacity-70">{{ countFor(filter.key) }}</span>
          </button>
        }
      </nav>
    }
  </header>

  @if (loadState() === 'loading') {
    <!-- Skeleton cards, matching the grid so the layout does not jump -->
    <div
      aria-busy="true"
      aria-label="Loading creator videos"
      class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    >
      @for (placeholder of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; track placeholder) {
        <div class="overflow-hidden rounded-xl border border-border bg-card">
          <div class="w-full animate-pulse bg-gray-200 dark:bg-white/10" style="aspect-ratio: 9 / 14"></div>
          <div class="flex flex-col gap-y-2 p-3">
            <div class="h-3.5 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
            <div class="h-3 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
          </div>
        </div>
      }
    </div>
  } @else if (loadState() === 'error') {
    <div
      role="alert"
      class="flex flex-col items-start gap-y-3 rounded-xl border border-destructive/30 bg-destructive/5 p-5"
    >
      <p class="m-0 text-sm font-semibold text-error">We could not load the video library</p>
      <p class="m-0 text-sm text-secondary">{{ loadError() }}</p>
      <button type="button" (click)="reload()" class="secondary-button">Try again</button>
    </div>
  } @else if (isEmptyLibrary()) {
    <div class="flex flex-col items-start gap-y-3 rounded-xl border border-border bg-card p-6">
      <h2 class="m-0 text-lg font-semibold text-primary">No creator posts yet</h2>
      <p class="m-0 max-w-xl text-sm text-secondary">
        Once your creators publish their TikTok posts for this brief, they appear here with their
        organic performance — ready to carry budget as Spark ads.
      </p>
    </div>
  } @else if (hasNoMatches()) {
    <div class="flex flex-col items-start gap-y-3 rounded-xl border border-border bg-card p-6">
      <h2 class="m-0 text-lg font-semibold text-primary">Nothing matches this filter</h2>
      <p class="m-0 text-sm text-secondary">
        {{ totalCount() }} posts are in the library. Try a different filter.
      </p>
      <button type="button" (click)="selectFilter('all')" class="secondary-button">
        Show all posts
      </button>
    </div>
  } @else {
    <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      @for (entry of visibleVideos(); track entry.videoId) {
        <sa-video-card [entry]="entry" (open)="onOpenVideo($event)" />
      }
    </div>
  }
</section>

<!-- Full-screen analysis modal; its Boosting tab holds the Spark auth + budget flow -->
@if (openVideo(); as video) {
  <ttor-video-analysis [video]="video" (close)="onCloseVideo()" />
}
